import { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { Map, LayoutGrid, Plus, ChevronDown, Wrench, UserPlus, MapPin } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useDispatch } from './hooks';
import type { DispatchJob } from './hooks';
import { QueuePanel } from './QueuePanel';
import { ActivePanel } from './ActivePanel';
import { CrewSidebar } from './CrewSidebar';
import { AssignModal } from './AssignModal';
import { DispatchDetailModal } from './DispatchDetailModal';
import { CreateJobPanel } from '../Jobs/CreateJobPanel';
import { SlidePanel } from '../../components/common/SlidePanel';
import { Skeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';

const DispatchMap = lazy(() => import('./DispatchMap').then(m => ({ default: m.DispatchMap })));

// Max panel height: viewport minus topbar (56px), page padding (28px top), header area (~80px)
const PANEL_MAX = 'calc(100vh - 56px - 28px - 80px)';

export function DispatchPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { queue, active, crew, loading, error, summary, assignJob, advanceStatus, reassignJob, cancelDispatch } = useDispatch(business?.id);
  const { features, isLensActive, industryName } = useIndustryLens();

  const [assignTarget, setAssignTarget] = useState<DispatchJob | null>(null);
  const [detailTarget, setDetailTarget] = useState<DispatchJob | null>(null);
  const [reassignTarget, setReassignTarget] = useState<DispatchJob | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [createOpen, setCreateOpen] = useState<'job' | 'client' | 'location' | null>(null);
  const [quickDropdown, setQuickDropdown] = useState(false);
  const quickRef = useRef<HTMLDivElement>(null);

  // Quick-create: client form state
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientSaving, setClientSaving] = useState(false);

  // Quick-create: location form state
  const [locName, setLocName] = useState('');
  const [locAddress, setLocAddress] = useState('');
  const [locSaving, setLocSaving] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    if (!quickDropdown) return;
    const handler = (e: MouseEvent) => { if (quickRef.current && !quickRef.current.contains(e.target as Node)) setQuickDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [quickDropdown]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleCreateClient = useCallback(async () => {
    if (!clientName.trim() || !business?.id) return;
    setClientSaving(true);
    const { error } = await supabase.from('clients').insert({ business_id: business.id, name: clientName.trim(), phone: clientPhone || null, email: clientEmail || null, source: 'manual' });
    setClientSaving(false);
    if (error) {
      setToast({ msg: `Couldn't create client: ${error.message}`, type: 'error' });
      return;
    }
    setClientName(''); setClientPhone(''); setClientEmail('');
    setCreateOpen(null);
    setToast({ msg: 'Client created', type: 'success' });
  }, [clientName, clientPhone, clientEmail, business?.id]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleCreateLocation = useCallback(async () => {
    if (!locName.trim() || !business?.id) return;
    setLocSaving(true);
    const { error } = await supabase.from('locations').insert({ business_id: business.id, name: locName.trim(), address: locAddress || null, type: 'job_site', status: 'active' });
    setLocSaving(false);
    if (error) {
      setToast({ msg: `Couldn't create location: ${error.message}`, type: 'error' });
      return;
    }
    setLocName(''); setLocAddress('');
    setCreateOpen(null);
    setToast({ msg: 'Location created', type: 'success' });
  }, [locName, locAddress, business?.id]);
  const [centerView, setCenterView] = useState<'board' | 'map'>(() => {
    try { return (localStorage.getItem('crewbit-dispatch-view') as 'board' | 'map') ?? 'map'; } catch { return 'map'; }
  });
  const setView = (v: 'board' | 'map') => {
    setCenterView(v);
    try { localStorage.setItem('crewbit-dispatch-view', v); } catch { /* */ }
  };

  useEffect(() => { document.title = 'CrewBit · Dispatch'; }, []);

  if (isLensActive && !features.showDispatch) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Dispatch</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Dispatch is not typically used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens to see this module, or exit the lens to return to your view.</div>
        </div>
      </div>
    );
  }

  const uid = user?.id ?? '';

  const handleAssign = async (jobId: string, userId: string) => {
    const ok = await assignJob(jobId, userId, uid);
    if (ok) { setToast({ msg: 'Job dispatched', type: 'success' }); setAssignTarget(null); }
    return ok;
  };

  const handleAdvance = async (jobId: string, status: string) => {
    const ok = await advanceStatus(jobId, status, uid);
    if (ok) setToast({ msg: `Status → ${status}`, type: 'success' });
  };

  const handleReassign = async (jobId: string, userId: string) => {
    const ok = await reassignJob(jobId, userId, uid);
    if (ok) { setToast({ msg: 'Job reassigned', type: 'success' }); setReassignTarget(null); }
    return ok;
  };

  const handleCancel = async (jobId: string) => {
    const ok = await cancelDispatch(jobId, uid);
    if (ok) { setToast({ msg: 'Dispatch cancelled', type: 'success' }); setDetailTarget(null); }
  };

  const panelStyle: React.CSSProperties = {
    background: palette.white, borderRadius: admin.cardRadius,
    border: `1px solid ${admin.border}`, boxShadow: shadow.card,
    padding: '14px 12px', maxHeight: PANEL_MAX, overflowY: 'auto',
  };

  if (loading) {
    return (
      <div>
        <Skeleton width={200} height={24} style={{ marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, alignItems: 'start' }}>
          {[1, 2, 3].map(i => <div key={i} style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><Skeleton height={16} style={{ marginBottom: 12 }} /><Skeleton height={60} style={{ marginBottom: 8 }} /><Skeleton height={60} /></div>)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Dispatch</div>
        <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header + Stats Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Dispatch</div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: palette.green, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: palette.greenWash }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: palette.green, display: 'inline-block', animation: 'crewbit-pulse 2s ease infinite' }} />
            Live
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, fontWeight: 600 }}>
            <span style={{ color: summary.queueCount > 5 ? palette.amber : palette.g500 }}>Queue: {summary.queueCount}</span>
            <span style={{ color: palette.accentDeep }}>Active: {summary.activeCount}</span>
            <span style={{ color: palette.green }}>Done: {summary.completedCount}</span>
            <span style={{ color: summary.crewAvailable < 2 ? palette.red : palette.g500 }}>Crew: {summary.crewAvailable}/{summary.crewTotal}</span>
          </div>

          {/* Quick-create dropdown */}
          <div ref={quickRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setQuickDropdown(!quickDropdown)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                borderRadius: 8, border: 'none', background: palette.accent,
                fontSize: 12.5, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
              }}
            ><Plus size={14} /> New <ChevronDown size={12} /></button>
            {quickDropdown && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, width: 180,
                background: palette.white, borderRadius: 10, border: `1px solid ${admin.border}`,
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden',
              }}>
                <button onClick={() => { setCreateOpen('job'); setQuickDropdown(false); }} style={{ width: '100%', padding: '9px 14px', border: 'none', background: 'transparent', fontSize: 13, color: palette.g700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g100)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                ><Wrench size={14} color={palette.g500} /> New job</button>
                <button onClick={() => { setCreateOpen('client'); setQuickDropdown(false); }} style={{ width: '100%', padding: '9px 14px', border: 'none', background: 'transparent', fontSize: 13, color: palette.g700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g100)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                ><UserPlus size={14} color={palette.g500} /> New client</button>
                <button onClick={() => { setCreateOpen('location'); setQuickDropdown(false); }} style={{ width: '100%', padding: '9px 14px', border: 'none', background: 'transparent', fontSize: 13, color: palette.g700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g100)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                ><MapPin size={14} color={palette.g500} /> New location</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: admin.caption, marginBottom: 16 }}>
        Command centre — {new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' })}
      </div>

      {/* 3-column grid: 20% / 55% / 25% — panels size to content, cap at max-height */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2.75fr 1.25fr', gap: 12, alignItems: 'start' }}>
        <div style={panelStyle}>
          <QueuePanel jobs={queue} onAssign={setAssignTarget} onSelect={setDetailTarget} />
        </div>
        <div style={{ ...panelStyle, padding: '14px 16px' }}>
          {/* Board / Map toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {centerView === 'board' ? 'Active Board' : 'Map View'}
            </span>
            <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 6, padding: 2 }}>
              <button onClick={() => setView('board')} style={{
                padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                background: centerView === 'board' ? palette.g800 : 'transparent',
                color: centerView === 'board' ? palette.white : palette.g500,
                transition: `all ${transition.fast}`,
              }}><LayoutGrid size={12} /> Board</button>
              <button onClick={() => setView('map')} style={{
                padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                background: centerView === 'map' ? palette.g800 : 'transparent',
                color: centerView === 'map' ? palette.white : palette.g500,
                transition: `all ${transition.fast}`,
              }}><Map size={12} /> Map</button>
            </div>
          </div>
          {centerView === 'board' ? (
            <ActivePanel jobs={active} onAdvance={handleAdvance} onSelect={setDetailTarget} />
          ) : (
            <Suspense fallback={<div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Skeleton width={80} height={14} /></div>}>
              <DispatchMap
                jobs={[...queue, ...active]}
                crew={crew}
                onJobClick={setDetailTarget}
                selectedJobId={detailTarget?.id ?? null}
              />
            </Suspense>
          )}
        </div>
        <div style={panelStyle}>
          <CrewSidebar crew={crew} onCrewClick={() => {}} />
        </div>
      </div>

      {/* Modals */}
      <AssignModal job={assignTarget ?? reassignTarget} crew={crew}
        onAssign={reassignTarget ? (jId, uId) => handleReassign(jId, uId) : handleAssign}
        onClose={() => { setAssignTarget(null); setReassignTarget(null); }} />

      <DispatchDetailModal job={detailTarget} onClose={() => setDetailTarget(null)}
        onAdvance={handleAdvance} onReassign={(j) => { setDetailTarget(null); setReassignTarget(j); }}
        onCancel={handleCancel} />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}

      {/* Quick-create panels */}
      <CreateJobPanel
        isOpen={createOpen === 'job'}
        onClose={() => setCreateOpen(null)}
        onCreate={async (data) => {
          if (!business?.id) return null;
          const { data: wo, error: e } = await supabase.from('work_orders').insert({ ...data, business_id: business.id, status: 'new', created_by: uid }).select('id').maybeSingle();
          if (e || !wo) return null;
          setToast({ msg: 'Job created', type: 'success' });
          return wo.id;
        }}
        users={crew.map(c => ({ id: c.id, first_name: c.first_name, last_name: c.last_name }))}
        userId={uid}
      />

      <SlidePanel isOpen={createOpen === 'client'} onClose={() => setCreateOpen(null)} title="New Client" subtitle="Quick add a client">
        <div style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Name *</label><input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Sarah Miller" /></div>
        <div style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Phone</label><input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} value={clientPhone} onChange={e => setClientPhone(e.target.value)} /></div>
        <div style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Email</label><input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} value={clientEmail} onChange={e => setClientEmail(e.target.value)} /></div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
          <button onClick={() => setCreateOpen(null)} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleCreateClient} disabled={clientSaving || !clientName.trim()} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: clientSaving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: clientSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{clientSaving ? 'Saving...' : 'Create Client'}</button>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={createOpen === 'location'} onClose={() => setCreateOpen(null)} title="New Location" subtitle="Quick add a location">
        <div style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Name *</label><input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} value={locName} onChange={e => setLocName(e.target.value)} placeholder="e.g. 42 Oak St" /></div>
        <div style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Address</label><input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} value={locAddress} onChange={e => setLocAddress(e.target.value)} placeholder="Full address" /></div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
          <button onClick={() => setCreateOpen(null)} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleCreateLocation} disabled={locSaving || !locName.trim()} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: locSaving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: locSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{locSaving ? 'Saving...' : 'Create Location'}</button>
        </div>
      </SlidePanel>
    </div>
  );
}
